import { Box, Button, Divider, Grid, InputAdornment, Paper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import { Email, Fingerprint, LockOutlined, VerifiedUser, Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { IsNullOrEmpty, getTokenValue } from '../../helpers/utility';
import { loginUserRequest } from '../../store/authentication/auth.actions';
import { getAccessToken } from '../../store/authentication/auth.selector';
import ChangePassword from '../profile/changePassword';
import Captcha, { generateCaptchaCode } from './Captcha';

const useStyles = makeStyles((theme) => ({
    card: {
        width: '100%',
        maxWidth: 420,
        margin: '0 auto',
        padding: theme.spacing(2.5),
        borderRadius: 12,
        textAlign: 'left',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(2, 1.5),
        },
    },
    badge: {
        width: 42,
        height: 42,
        borderRadius: '50%',
        backgroundColor: theme.palette.secondary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        '& svg': {
            fontSize: 22,
        },
    },
    title: {
        marginTop: theme.spacing(1),
        fontSize: '1.15rem',
        fontWeight: 700,
    },
    subtitle: {
        color: 'rgba(255, 255, 255, 0.7)',
        marginTop: theme.spacing(0.25),
        fontSize: '0.75rem',
    },
    divider: {
        margin: theme.spacing(1.5, 0),
        backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    captchaRow: {
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
    },
    captchaField: {
        flex: '1 1 140px',
        minWidth: 140,
        marginRight: theme.spacing(1),
    },
    submit: {
        padding: theme.spacing(1),
        fontSize: 14,
        fontWeight: 600,
    },
    reset: {
        padding: theme.spacing(1),
        fontSize: 14,
    },
    footerLink: {
        marginTop: theme.spacing(1.5),
        fontSize: '0.8rem',
        textAlign: 'center',
    },
}));

const emptyValues = {
    userName: "",
    password: "",
    captcha: "",
};

export default function Login() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const [values, setValues] = useState(emptyValues);
    const [captchaCode, setCaptchaCode] = useState(generateCaptchaCode);
    const [isValidated, setValidated] = useState(false);

    const { accessToken } = useSelector((state) => {
        return {
            accessToken: getAccessToken(state),
        }
    });

    useEffect(() => {
        if (!IsNullOrEmpty(accessToken)) {
            const user = getTokenValue(accessToken);
            if (user.isResetPassword) {
                setOpen(true);
            }
            else {
                navigate("/");
            }
        }
    }, [accessToken]);

    const isCaptchaValid = () => {
        return !IsNullOrEmpty(values.captcha) &&
            values.captcha.trim().toUpperCase() === captchaCode;
    }

    const validate = () => {
        if (IsNullOrEmpty(values.userName)) {
            return false;
        }
        if (IsNullOrEmpty(values.password)) {
            return false;
        }
        if (!isCaptchaValid()) {
            return false;
        }
        return true;
    }

    const refreshCaptcha = () => {
        setCaptchaCode(generateCaptchaCode());
        setValues((current) => ({ ...current, captcha: "" }));
    }

    const handleReset = () => {
        setValidated(false);
        setValues(emptyValues);
        setCaptchaCode(generateCaptchaCode());
    }

    const handleSave = () => {
        if (validate()) {
            dispatch(loginUserRequest({
                userName: values.userName,
                password: values.password
            }))
        }
        else {
            setValidated(true);
            refreshCaptcha();
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        handleSave();
    }

    const captchaError = isValidated && !isCaptchaValid();

    return (
        <Paper className={classes.card} elevation={6}>
            {
                open && <ChangePassword />
            }
            <Box textAlign="center">
                <div className={classes.badge}>
                    <LockOutlined />
                </div>
                <Typography variant="h5" className={classes.title}>
                    Admin Login
                </Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    Sign in to manage your Team369 admin panel
                </Typography>
            </Box>

            <Divider className={classes.divider} />

            <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            variant='outlined'
                            size="small"
                            placeholder='Email'
                            label="Email"
                            type="email"
                            autoComplete="username"
                            autoFocus
                            value={values.userName}
                            onChange={(e) => setValues({ ...values, userName: e.target.value })}
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>
                                    <Email color='disabled' />
                                </InputAdornment>
                            }}
                            error={isValidated && IsNullOrEmpty(values.userName)}
                            helperText={isValidated && IsNullOrEmpty(values.userName) ? "User Name should be valid" : ""}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            variant='outlined'
                            size="small"
                            placeholder='Password'
                            label="Password"
                            type={visible ? "text" : 'password'}
                            autoComplete="current-password"
                            value={values.password}
                            onChange={(e) => setValues({ ...values, password: e.target.value })}
                            fullWidth
                            error={isValidated && IsNullOrEmpty(values.password)}
                            helperText={isValidated && IsNullOrEmpty(values.password) ? "Password should be valid" : ""}
                            InputProps={{
                                startAdornment: <InputAdornment position='start'>
                                    <Fingerprint color='disabled' />
                                </InputAdornment>,
                                endAdornment: <InputAdornment position='end'>
                                    {
                                        visible ? <Visibility color='disabled' style={{ cursor: 'pointer' }} onClick={() => setVisible(false)} /> :
                                            <VisibilityOff color='disabled' style={{ cursor: 'pointer' }} onClick={() => setVisible(true)} />
                                    }
                                </InputAdornment>
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.captchaRow}>
                            <TextField
                                variant='outlined'
                                size="small"
                                placeholder='Enter the code'
                                label="Captcha"
                                autoComplete="off"
                                inputProps={{ maxLength: 8 }}
                                className={classes.captchaField}
                                value={values.captcha}
                                onChange={(e) => setValues({ ...values, captcha: e.target.value })}
                                error={captchaError}
                                helperText={captchaError ? "Captcha does not match" : ""}
                                InputProps={{
                                    startAdornment: <InputAdornment position='start'>
                                        <VerifiedUser color='disabled' />
                                    </InputAdornment>
                                }}
                            />
                            <Captcha code={captchaCode} onRefresh={refreshCaptcha} />
                        </div>
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <Button
                            type="submit"
                            startIcon={<LockOutlined />}
                            variant='contained'
                            fullWidth
                            color="primary"
                            className={classes.submit}
                        >
                            Login
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <Button
                            type="button"
                            variant='outlined'
                            fullWidth
                            color="secondary"
                            className={classes.reset}
                            onClick={() => handleReset()}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <Typography variant="body2" className={classes.footerLink}>
                <Link to="/reset">
                    {"Forgot Password"}
                </Link>
            </Typography>
        </Paper>
    );
}
